import { useState, useRef, useCallback } from 'react';
import { chatApi } from '../services/api';

// The question a pictogram is drawn for: the student's message just before the answer.
// Falls back to the start of the answer itself (e.g. when the student only uploaded an image).
function questionFor(messages, idx) {
  for (let i = idx - 1; i >= 0; i--) {
    const m = messages[i];
    if (m?.role !== 'user') continue;
    const text = (m.content || '').trim();
    if (text && text !== '[Image uploaded]') return text;
    break;
  }
  return (messages[idx]?.content || '').replace(/[#*_>`]/g, '').trim().slice(0, 500);
}

// Pictogram from the GPU pictogram service (via our server). If the service is unreachable
// or errors, onFallback(idx) opens the old in-browser pictogram view instead.
export function usePictogram({ messages, onFallback }) {
  const [pictogram, setPictogram] = useState(null);   // { image, attributions }
  const [loadingIdx, setLoadingIdx] = useState(null);
  const cache = useRef({});                            // question text → result
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const fallbackRef = useRef(onFallback);
  fallbackRef.current = onFallback;

  const openPictogram = useCallback(async (idx) => {
    const question = questionFor(messagesRef.current, idx);
    if (!question) { fallbackRef.current(idx); return; }
    if (cache.current[question]) { setPictogram(cache.current[question]); return; }
    setLoadingIdx(idx);
    try {
      const res = await chatApi.createPictogram(question);
      cache.current[question] = res.data;
      setPictogram(res.data);
    } catch {
      fallbackRef.current(idx);
    } finally {
      setLoadingIdx(null);
    }
  }, []);

  const closePictogram = useCallback(() => setPictogram(null), []);

  return { pictogram, loadingIdx, openPictogram, closePictogram };
}
